import './styles.css';

const MAX_FILES = 8;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_TOTAL_BYTES = 64 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;
const MAX_PREVIEW_EDGE = 1024;

const elements = {
  fileInput: document.querySelector('#file-input'),
  dropZone: document.querySelector('#drop-zone'),
  queueList: document.querySelector('#queue-list'),
  queueCount: document.querySelector('#queue-count'),
  queueSummary: document.querySelector('#queue-summary'),
  convertButton: document.querySelector('#convert-button'),
  clearButton: document.querySelector('#clear-button'),
  resultPanel: document.querySelector('#result-panel'),
  resultSummary: document.querySelector('#result-summary'),
  zipButton: document.querySelector('#zip-button'),
  status: document.querySelector('#status-message'),
  qualityInput: document.querySelector('#quality-input'),
  qualityValue: document.querySelector('#quality-value'),
  qualityControl: document.querySelector('#quality-control'),
};

const state = {
  queue: [],
  format: 'jpg',
  quality: 0.88,
  activeRun: null,
  revision: 0,
  inputTicket: 0,
  nextRunId: 0,
};

document.querySelectorAll('[data-year]').forEach((node) => {
  node.textContent = String(new Date().getFullYear());
});

function ascii(bytes, start, length) {
  return String.fromCharCode(...bytes.slice(start, start + length));
}

function readU32(bytes, offset) {
  return (bytes[offset] + (bytes[offset + 1] * 0x100) + (bytes[offset + 2] * 0x10000) + (bytes[offset + 3] * 0x1000000)) >>> 0;
}

function readU24(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
}

function readVp8Dimensions(payload) {
  if (payload.length < 10 || payload[3] !== 0x9d || payload[4] !== 0x01 || payload[5] !== 0x2a) {
    throw new Error('The WebP image payload is malformed.');
  }
  return {
    width: (payload[6] | (payload[7] << 8)) & 0x3fff,
    height: (payload[8] | (payload[9] << 8)) & 0x3fff,
  };
}

function readVp8lDimensions(payload) {
  if (payload.length < 5 || payload[0] !== 0x2f) {
    throw new Error('The WebP lossless payload is malformed.');
  }
  return {
    width: 1 + ((payload[1] | (payload[2] << 8)) & 0x3fff),
    height: 1 + (((payload[2] >> 6) | (payload[3] << 2) | ((payload[4] & 0x3f) << 10)) & 0x3fff),
  };
}

function parseWebp(bytes) {
  if (bytes.length < 20 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') {
    throw new Error('This does not look like a WebP file. The file contents were checked.');
  }

  const riffSize = readU32(bytes, 4);
  const containerEnd = 8 + riffSize;
  if (riffSize < 4 || containerEnd > bytes.length || containerEnd < 20) {
    throw new Error('The WebP container is incomplete or damaged.');
  }

  let offset = 12;
  let hasImagePayload = false;
  let animated = false;
  let canvasWidth = 0;
  let canvasHeight = 0;
  let frameWidth = 0;
  let frameHeight = 0;

  while (offset < containerEnd) {
    if (offset + 8 > containerEnd) throw new Error('The WebP container has an incomplete chunk.');
    const type = ascii(bytes, offset, 4);
    const size = readU32(bytes, offset + 4);
    const payloadStart = offset + 8;
    if (size > containerEnd - payloadStart) throw new Error('The WebP container has a chunk outside its declared bounds.');
    const payloadEnd = payloadStart + size;
    const payload = bytes.subarray(payloadStart, payloadEnd);

    if (type === 'ANIM' || type === 'ANMF') animated = true;
    if (type === 'VP8X') {
      if (size < 10) throw new Error('The WebP extended header is incomplete.');
      if ((payload[0] & 0x02) !== 0) animated = true;
      canvasWidth = 1 + readU24(payload, 4);
      canvasHeight = 1 + readU24(payload, 7);
    } else if (type === 'VP8 ') {
      const dimensions = readVp8Dimensions(payload);
      frameWidth ||= dimensions.width;
      frameHeight ||= dimensions.height;
      hasImagePayload = true;
    } else if (type === 'VP8L') {
      const dimensions = readVp8lDimensions(payload);
      frameWidth ||= dimensions.width;
      frameHeight ||= dimensions.height;
      hasImagePayload = true;
    }

    offset = payloadEnd + (size % 2);
  }

  if (offset !== containerEnd) throw new Error('The WebP container has invalid chunk padding.');
  if (animated) throw new Error('Animated WebP is not supported yet. Choose a static WebP image.');
  if (!hasImagePayload || !frameWidth || !frameHeight) throw new Error('No supported static WebP image was found in this file.');
  if (canvasWidth && canvasHeight && (canvasWidth !== frameWidth || canvasHeight !== frameHeight)) {
    throw new Error('The WebP canvas dimensions do not match its image frame.');
  }
  const width = frameWidth || canvasWidth;
  const height = frameHeight || canvasHeight;
  if (width * height > MAX_PIXELS) throw new Error(`The decoded image is larger than ${MAX_PIXELS / 1000000} megapixels.`);

  return { width, height, animated: false };
}

async function validateFile(file) {
  if (!file || file.size === 0) throw new Error('The file is empty.');
  if (file.size > MAX_FILE_BYTES) throw new Error(`The file is larger than ${MAX_FILE_BYTES / (1024 * 1024)} MB.`);
  const bytes = new Uint8Array(await file.arrayBuffer());
  return parseWebp(bytes);
}

function getStatusLabel(status) {
  return { queued: 'Ready', working: 'Converting…', success: 'Converted', error: 'Could not convert' }[status] || status;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function setStatus(message, kind = '') {
  elements.status.textContent = message;
  elements.status.className = `status-message${kind ? ` ${kind}` : ''}`;
}

function makeEntry(file) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file,
    status: 'queued',
    error: '',
    metadata: null,
    outputBlob: null,
    outputUrl: null,
    previewUrl: null,
    outputSize: 0,
    width: 0,
    height: 0,
    runId: 0,
  };
}

function revokeEntry(entry) {
  if (entry.outputUrl) URL.revokeObjectURL(entry.outputUrl);
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
  entry.outputUrl = null;
  entry.previewUrl = null;
  entry.outputBlob = null;
  entry.outputSize = 0;
}

function totalBytes() {
  return state.queue.reduce((sum, entry) => sum + entry.file.size, 0);
}

function outputExtension(format) {
  return format === 'jpg' ? '.jpg' : '.png';
}

function outputName(entry, format = state.format) {
  const base = entry.file.name.replace(/\.[^.]+$/, '') || 'converted-webp';
  return `${base}${outputExtension(format)}`;
}

function buildQueueItem(entry) {
  const item = document.createElement('article');
  item.className = 'queue-item';
  item.dataset.id = entry.id;

  const thumb = document.createElement('div');
  thumb.className = 'queue-thumb';
  if (entry.previewUrl) {
    const image = document.createElement('img');
    image.src = entry.previewUrl;
    image.alt = `Preview of ${entry.file.name}`;
    thumb.append(image);
  } else {
    thumb.textContent = entry.file.name.toLowerCase().endsWith('.webp') ? 'WEBP' : 'FILE';
  }

  const details = document.createElement('div');
  details.className = 'queue-details';
  const name = document.createElement('div');
  name.className = 'queue-name';
  name.title = entry.file.name;
  name.textContent = entry.file.name;
  const meta = document.createElement('div');
  meta.className = 'queue-meta';
  const dimensions = entry.width && entry.height ? ` · ${entry.width} × ${entry.height}` : '';
  const outputSize = entry.status === 'success' && entry.outputSize ? ` · output ${formatBytes(entry.outputSize)}` : '';
  meta.textContent = `${formatBytes(entry.file.size)}${dimensions}${outputSize}`;
  const status = document.createElement('div');
  status.className = `queue-status ${entry.status}`;
  status.textContent = entry.status === 'error' ? entry.error : getStatusLabel(entry.status);
  details.append(name, meta, status);

  const actions = document.createElement('div');
  actions.className = 'queue-actions';
  if (entry.status === 'success') {
    const download = document.createElement('button');
    download.className = 'mini-button';
    download.type = 'button';
    download.textContent = 'Download';
    download.addEventListener('click', () => downloadEntry(entry));
    actions.append(download);

    const reconvert = document.createElement('button');
    reconvert.className = 'mini-button';
    reconvert.type = 'button';
    reconvert.textContent = 'Convert again';
    reconvert.addEventListener('click', () => reconvertEntry(entry));
    actions.append(reconvert);
  }
  if (entry.status === 'error') {
    const retry = document.createElement('button');
    retry.className = 'mini-button';
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => retryEntry(entry));
    actions.append(retry);
  }
  const remove = document.createElement('button');
  remove.className = 'mini-button';
  remove.type = 'button';
  remove.textContent = 'Remove';
  remove.setAttribute('aria-label', `Remove ${entry.file.name}`);
  remove.addEventListener('click', () => removeEntry(entry.id));
  actions.append(remove);

  item.append(thumb, details, actions);
  return item;
}

function renderQueue() {
  elements.queueList.replaceChildren(...state.queue.map(buildQueueItem));
  const ready = state.queue.filter((entry) => entry.status === 'queued' && entry.metadata).length;
  const working = state.queue.filter((entry) => entry.status === 'working').length;
  const success = state.queue.filter((entry) => entry.status === 'success').length;
  const failed = state.queue.filter((entry) => entry.status === 'error').length;
  elements.queueCount.textContent = String(state.queue.length);
  elements.queueSummary.textContent = state.queue.length === 0
    ? 'Add a file to begin.'
    : `${ready} ready · ${working} working · ${success} complete${failed ? ` · ${failed} failed` : ''}`;
  elements.convertButton.disabled = ready === 0 || working > 0 || Boolean(state.activeRun);
  elements.clearButton.disabled = state.queue.length === 0;
  const settingLocked = working > 0 || Boolean(state.activeRun);
  document.querySelectorAll('input[name="output-format"]').forEach((input) => { input.disabled = settingLocked; });
  elements.qualityInput.disabled = settingLocked || state.format === 'png';
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  elements.resultPanel.hidden = successes.length === 0;
  elements.resultSummary.textContent = successes.length
    ? `${successes.length} ${successes.length === 1 ? 'file is' : 'files are'} ready as ${state.format.toUpperCase()}.`
    : '';
}

function cancelActiveRun() {
  if (state.activeRun) state.activeRun.cancelled = true;
}

function removeEntry(id) {
  const index = state.queue.findIndex((entry) => entry.id === id);
  if (index < 0) return;
  revokeEntry(state.queue[index]);
  state.queue.splice(index, 1);
  state.revision += 1;
  renderQueue();
  setStatus('File removed from the queue. Other files keep their current work.');
}

function clearQueue() {
  state.inputTicket += 1;
  cancelActiveRun();
  state.queue.forEach(revokeEntry);
  state.queue = [];
  state.revision += 1;
  renderQueue();
  setStatus('Queue cleared. Your originals were never uploaded.');
}

async function addFiles(fileList) {
  const incoming = [...fileList];
  if (!incoming.length) return;
  const ticket = ++state.inputTicket;
  if (state.queue.length + incoming.length > MAX_FILES) {
    setStatus(`A batch can contain up to ${MAX_FILES} files.`, 'error');
    return;
  }
  if (totalBytes() + incoming.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus(`Keep the total batch below ${MAX_TOTAL_BYTES / (1024 * 1024)} MB.`, 'error');
    return;
  }

  const entries = [];
  for (const file of incoming) {
    if (ticket !== state.inputTicket) return;
    const entry = makeEntry(file);
    try {
      entry.metadata = await validateFile(file);
      entry.width = entry.metadata.width;
      entry.height = entry.metadata.height;
    } catch (error) {
      entry.status = 'error';
      entry.error = error instanceof Error ? error.message : 'The file could not be checked.';
    }
    entries.push(entry);
  }

  if (ticket !== state.inputTicket) return;
  if (state.queue.length + entries.length > MAX_FILES || totalBytes() + entries.reduce((sum, entry) => sum + entry.file.size, 0) > MAX_TOTAL_BYTES) {
    setStatus('The queue changed while the files were being checked. Add fewer files and try again.', 'error');
    return;
  }
  state.queue.push(...entries);
  state.revision += 1;
  renderQueue();
  if (state.activeRun && state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) {
    setStatus('Previous conversions are finishing. Convert all will be available shortly.');
  } else if (state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) {
    setStatus('Files are ready. Choose Convert all when you are ready.');
  }
  if (state.queue.some((entry) => entry.status === 'error')) setStatus('Some files need attention before conversion.', 'error');
  elements.fileInput.value = '';
}

function canvasBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The browser could not create the output image.')), type, quality);
  });
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = 'async';
  image.src = sourceUrl;
  try {
    await image.decode();
    return { image, width: image.naturalWidth, height: image.naturalHeight, close: () => { image.src = ''; } };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

function assertOutputBlob(blob, format) {
  const expectedType = format === 'jpg' ? 'image/jpeg' : 'image/png';
  if (!blob || blob.type !== expectedType) throw new Error('The browser returned an unexpected output type.');
  return blob.slice(0, 12).arrayBuffer().then((buffer) => {
    const bytes = new Uint8Array(buffer);
    const valid = format === 'jpg'
      ? bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
      : bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a;
    if (!valid) throw new Error('The browser returned an invalid output image.');
  });
}

function isActive(entry, run) {
  return state.activeRun === run && !run.cancelled && state.queue.includes(entry) && entry.runId === run.id;
}

async function createPreviewUrl(blob, width, height, format, quality) {
  if (Math.max(width, height) <= MAX_PREVIEW_EDGE || typeof createImageBitmap !== 'function') return URL.createObjectURL(blob);
  const scale = MAX_PREVIEW_EDGE / Math.max(width, height);
  const previewWidth = Math.max(1, Math.round(width * scale));
  const previewHeight = Math.max(1, Math.round(height * scale));
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = previewWidth;
  canvas.height = previewHeight;
  try {
    canvas.getContext('2d', { alpha: true }).drawImage(bitmap, 0, 0, previewWidth, previewHeight);
    const previewBlob = await canvasBlob(canvas, format === 'jpg' ? 'image/jpeg' : 'image/png', format === 'jpg' ? quality : undefined);
    await assertOutputBlob(previewBlob, format);
    return URL.createObjectURL(previewBlob);
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
}

async function convertEntry(entry, run) {
  if (!isActive(entry, run) || !entry.metadata) return;
  entry.runId = run.id;
  entry.status = 'working';
  entry.error = '';
  renderQueue();
  let image = null;
  try {
    image = await decodeImage(entry.file);
    if (!isActive(entry, run)) {
      return;
    }
    const width = image.width || image.naturalWidth;
    const height = image.height || image.naturalHeight;
    if (!width || !height || width * height > MAX_PIXELS) throw new Error(`The decoded image is larger than ${MAX_PIXELS / 1000000} megapixels.`);
    entry.width = width;
    entry.height = height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    try {
      const context = canvas.getContext('2d', { alpha: true });
      if (run.format === 'jpg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(image.image || image, 0, 0, width, height);
      const blob = await canvasBlob(canvas, run.format === 'jpg' ? 'image/jpeg' : 'image/png', run.format === 'jpg' ? run.quality : undefined);
      await assertOutputBlob(blob, run.format);
      if (!isActive(entry, run)) return;
      const previewUrl = await createPreviewUrl(blob, width, height, run.format, run.quality);
      if (!isActive(entry, run)) {
        URL.revokeObjectURL(previewUrl);
        return;
      }
      revokeEntry(entry);
      entry.outputBlob = blob;
      entry.outputSize = blob.size;
      entry.outputUrl = URL.createObjectURL(blob);
      entry.previewUrl = previewUrl;
      entry.status = 'success';
      state.revision += 1;
    } finally {
      canvas.width = 0;
      canvas.height = 0;
    }
  } catch (error) {
    if (!isActive(entry, run)) return;
    entry.status = 'error';
    entry.error = error instanceof Error ? error.message : 'The file could not be converted.';
    state.revision += 1;
  } finally {
    image?.close?.();
  }
  renderQueue();
}

async function convertAll() {
  if (state.activeRun) return;
  const pending = state.queue.filter((entry) => entry.status === 'queued' && entry.metadata);
  if (!pending.length) return;
  const run = {
    id: ++state.nextRunId,
    format: state.format,
    quality: state.quality,
    cancelled: false,
  };
  state.activeRun = run;
  pending.forEach((entry) => { entry.runId = run.id; });
  setStatus('Converting locally. Your files stay in this browser tab.');
  let cursor = 0;
  const worker = async () => {
    while (!run.cancelled) {
      const entry = pending[cursor];
      cursor += 1;
      if (!entry) return;
      await convertEntry(entry, run);
    }
  };
  await Promise.all([worker(), worker()]);
  if (run.cancelled) {
    if (state.activeRun === run) state.activeRun = null;
    renderQueue();
    if (state.queue.some((entry) => entry.status === 'queued' && entry.metadata)) {
      setStatus('Previous conversions finished. Choose Convert all when you are ready.');
    }
    return;
  }
  if (state.activeRun !== run) return;
  state.activeRun = null;
  const succeeded = state.queue.filter((entry) => entry.status === 'success').length;
  const failed = state.queue.filter((entry) => entry.status === 'error').length;
  setStatus(failed ? `${succeeded} converted; ${failed} need attention.` : `${succeeded} ${succeeded === 1 ? 'file is' : 'files are'} ready to download.`);
  renderQueue();
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadEntry(entry) {
  if (entry.outputBlob) downloadBlob(entry.outputBlob, outputName(entry));
}

async function downloadZip() {
  const successes = state.queue.filter((entry) => entry.status === 'success' && entry.outputBlob);
  if (!successes.length) return;
  const revision = state.revision;
  elements.zipButton.disabled = true;
  setStatus('Building the ZIP locally…');
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const usedNames = new Set();
    successes.forEach((entry) => {
      const original = outputName(entry);
      let name = original;
      let suffix = 2;
      while (usedNames.has(name)) name = `${original.replace(/\.[^.]+$/, '')}-${suffix++}${outputExtension(state.format)}`;
      usedNames.add(name);
      zip.file(name, entry.outputBlob);
    });
    const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
    if (revision !== state.revision) {
      setStatus('ZIP canceled because the queue changed.');
      return;
    }
    downloadBlob(blob, `kivoza-webp-converted-${state.format}.zip`);
    setStatus('ZIP downloaded. It was created in this browser tab.');
  } catch (error) {
    setStatus(error instanceof Error ? error.message : 'The ZIP could not be created.', 'error');
  } finally {
    elements.zipButton.disabled = false;
  }
}

async function retryEntry(entry) {
  if (!state.queue.includes(entry) || state.activeRun) return;
  const ticket = ++state.inputTicket;
  setStatus(`Checking ${entry.file.name} again…`);
  try {
    const metadata = await validateFile(entry.file);
    if (ticket !== state.inputTicket || !state.queue.includes(entry)) return;
    entry.metadata = metadata;
    entry.width = metadata.width;
    entry.height = metadata.height;
    entry.error = '';
    entry.status = 'queued';
    state.revision += 1;
    renderQueue();
    await convertAll();
  } catch (error) {
    if (ticket !== state.inputTicket || !state.queue.includes(entry)) return;
    entry.metadata = null;
    entry.status = 'error';
    entry.error = error instanceof Error ? error.message : 'The file could not be checked.';
    state.revision += 1;
    renderQueue();
    setStatus('The file still needs attention.', 'error');
  }
}

function reconvertEntry(entry) {
  if (!state.queue.includes(entry) || state.activeRun) return;
  revokeEntry(entry);
  entry.status = 'queued';
  entry.error = '';
  state.revision += 1;
  renderQueue();
  convertAll();
}

function resetForSettingChange() {
  if (state.activeRun) return;
  state.queue.forEach((entry) => {
    if (entry.status === 'error' && !entry.metadata) return;
    revokeEntry(entry);
    entry.status = 'queued';
    entry.error = '';
    entry.width = entry.metadata?.width || 0;
    entry.height = entry.metadata?.height || 0;
  });
  state.revision += 1;
  renderQueue();
  if (state.queue.length) setStatus('Output settings changed. Convert the queue again to create new files.');
}

elements.fileInput.addEventListener('change', (event) => addFiles(event.target.files));
elements.dropZone.addEventListener('click', () => elements.fileInput.click());
elements.dropZone.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    elements.fileInput.click();
  }
});
['dragenter', 'dragover'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.add('is-dragging');
}));
['dragleave', 'drop'].forEach((eventName) => elements.dropZone.addEventListener(eventName, (event) => {
  event.preventDefault();
  elements.dropZone.classList.remove('is-dragging');
}));
elements.dropZone.addEventListener('drop', (event) => addFiles(event.dataTransfer.files));
elements.convertButton.addEventListener('click', convertAll);
elements.clearButton.addEventListener('click', clearQueue);
elements.zipButton.addEventListener('click', downloadZip);
elements.qualityInput.addEventListener('input', () => {
  state.quality = Number(elements.qualityInput.value) / 100;
  elements.qualityValue.value = elements.qualityInput.value;
  elements.qualityValue.textContent = elements.qualityInput.value;
});
elements.qualityInput.addEventListener('change', resetForSettingChange);
document.querySelectorAll('input[name="output-format"]').forEach((input) => input.addEventListener('change', () => {
  state.format = document.querySelector('input[name="output-format"]:checked')?.value || 'jpg';
  elements.qualityControl.hidden = state.format === 'png';
  resetForSettingChange();
}));

renderQueue();
